import React, { useEffect, useRef, useState } from 'react';
import { Play, RotateCcw, Maximize2, Minimize2 } from 'lucide-react';
import { MONO } from './ui';
import { useYouTubeClip } from './useYouTubeClip';
import { LiveTranscript } from './LiveTranscript';
import { RCounter } from './RCounter';
import { Leaderboard } from './Leaderboard';

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const pill = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors';

// Click-to-load YouTube clip (no third-party request until the user presses play).
export const VideoClip = ({ v, dark = true, t, L, playToken = 0, onEnded, onManual, pron, pronNote, pronLabels, labDialect, slug }) => {
  const [on, setOn] = useState(false);
  const [taps, setTaps] = useState([]);
  const [finished, setFinished] = useState(false);
  const [big, setBig] = useState(false);
  const box = useRef(null);
  const end = () => { setFinished(true); onEnded?.(); };
  const { host, time, replay } = useYouTubeClip({ active: on, videoId: v?.youtube, start: v?.start || 0, end: v?.end, onEnded: end });
  const fire = useRef(null);
  fire.current = () => { setTaps([]); setFinished(false); if (!on) setOn(true); else replay(); };
  useEffect(() => { if (playToken) fire.current(); }, [playToken]);
  useEffect(() => {
    const exit = () => { if (!document.fullscreenElement) setBig(false); };
    document.addEventListener('fullscreenchange', exit);
    return () => document.removeEventListener('fullscreenchange', exit);
  }, []);

  useEffect(() => {
    if (!big) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setBig(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [big]);

  if (!v) return null;
  const toggleBig = () => {
    if (big) { if (document.fullscreenElement) document.exitFullscreen(); setBig(false); return; }
    setBig(true);
    document.documentElement.requestFullscreen?.().catch(() => {});
  };
  const isR = v.kind === 'r';
  return (
    <figure ref={box} className={big ? 'fixed inset-0 z-[200] bg-[#05091a] overflow-y-auto p-6 lg:p-10 space-y-5' : 'space-y-2'} data-testid={`video-${v.id}`}>
      <div className={`relative aspect-video rounded-2xl overflow-hidden bg-black border border-white/10 shadow-xl ${big ? 'max-h-[62vh] mx-auto' : ''}`}>
        {on ? (
          <div className="absolute inset-0 [&>iframe]:w-full [&>iframe]:h-full" data-testid={`video-iframe-${v.id}`}><div ref={host} /></div>
        ) : (
          <button onClick={() => { onManual?.(); fire.current(); }} className="group absolute inset-0 w-full h-full" data-testid={`video-play-${v.id}`} aria-label={v.title}>
            <img src={`https://i.ytimg.com/vi/${v.youtube}/hqdefault.jpg`} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full bg-blue-600 group-hover:scale-110 transition-transform flex items-center justify-center shadow-2xl"><Play className="w-7 h-7 text-white fill-white ml-1" /></span>
            </span>
            <span className="absolute left-3 top-3 px-2.5 py-1 rounded-lg bg-black/70 text-white text-xs font-bold">{v.label}</span>
            {(v.start > 0 || v.end) && <span className="absolute right-3 bottom-3 px-2 py-1 rounded bg-black/70 text-white text-[11px]" style={MONO}>{fmt(v.start || 0)}{v.end ? ` – ${fmt(v.end)}` : ''}</span>}
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => { onManual?.(); fire.current(); }} className={pill} data-testid={`video-replay-${v.id}`}><RotateCcw className="w-3.5 h-3.5" />{t.replaySegment}</button>
        <button onClick={toggleBig} className={pill} data-testid={`video-classroom-${v.id}`}>{big ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}{big ? t.exitClassroom : t.classroom}</button>
        <span className="text-[11px] text-blue-100/60" style={MONO}>{fmt(v.start || 0)}{v.end ? ` – ${fmt(v.end)}` : ''}</span>
      </div>
      {isR && <RCounter lines={v.words} taps={taps} onTap={() => setTaps((p) => [...p, time])} finished={finished} started={on} onRetry={() => fire.current()} t={t} big={big} slug={slug} clipId={v.id} />}
      {isR && <Leaderboard slug={slug} clipId={v.id} t={t} big={big} refreshKey={finished} title={v.label} />}
      {L && <LiveTranscript lines={v.words} time={time} started={on} focus={v.focus ? L(v.focus) : ''} testid={`transcript-${v.id}`} big={big} pron={pron} pronNote={pronNote} pronLabels={pronLabels} labDialect={labDialect} t={t} />}
      <figcaption className={`text-xs ${dark ? 'text-blue-100/70' : 'text-slate-500'}`}>
        <span className="font-semibold">{v.title}</span> · {t.source}: {v.channel} ·{' '}
        <a href={`https://www.youtube.com/watch?v=${v.youtube}&t=${v.start || 0}s`} target="_blank" rel="noopener noreferrer" className="underline" data-testid={`video-link-${v.id}`}>YouTube</a>
      </figcaption>
    </figure>
  );
};
